import { DragDropRejected, getMediaFilesFromFileEntries } from '@lib/files/drag-files';
import { useModalStore } from '@stores/ModalStore';
import { notificationStore } from '@stores/Notifications';
import { trackingStore } from '@stores/Tracking';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { runInAction } from 'mobx';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

export const useDragDropUpload = (
  enableMouseLeaveHandler: boolean = false,
  onDragEnter?: () => void,
  onDragLeave?: () => void,
  onDrop?: () => void,
  onFilesDropped?: (files: File[], rejected: DragDropRejected[]) => void,
) => {
  const [isDragging, setIsDragging] = useState(false);
  const uploadManager = useUploadManager();
  const modalStore = useModalStore();
  const dragCounterRef = useRef(0);
  // Tracks whether the current drag is an OS-level file drag (e.g. from Finder/Explorer).
  // During OS file drags, mouseenter events report buttons===0, which would falsely
  // trigger the "abandoned drag" fallback and kill the drag before drop fires.
  const isFileDragRef = useRef(false);
  // A drag that started on this page — a media card or row on its way to a folder, or just an
  // <img> — is never an upload, whatever types the browser attaches to it. `dragstart` only fires
  // for drags that begin in this document, so it is the one reliable marker; an OS drag from
  // Finder never raises it.
  const isInternalDragRef = useRef(false);

  const pushRejectedRichEvent = (rejected: DragDropRejected[]) => {
    if (rejected.length === 0) return;
    const isSingle = rejected.length === 1;
    const single = isSingle ? rejected[0] : null;
    notificationStore.pushRichEvent({
      type: 'rich-event',
      variant: 'error',
      iconKind: 'file-rejected',
      header: 'All dragged files rejected',
      description: isSingle
        ? // 1 file: name first (truncated by CSS) + the reason if there is one.
          single!.reason
          ? `{filename}, the file you dragged was rejected: ${single!.reason}.`
          : '{filename}, the file you dragged was rejected.'
        : // N files: "N files" is clickable (opens the modal) — see
          // `descriptionLinkText` below.
          `All ${rejected.length} files you dragged were rejected.`,
      descriptionFilename: isSingle ? single!.file.name : undefined,
      descriptionLinkText: isSingle ? undefined : `${rejected.length} files`,
      rows: rejected.map((r, i) => ({
        key: `${r.file.name}-${i}`,
        filename: r.file.name,
        reason: r.reason,
      })),
      modalTitle: 'Rejected files',
      viewLinkAction: isSingle ? undefined : `View ${rejected.length} rejected files`,
    });
    trackingStore.uploadRichEventShown({
      kind: 'file-rejected',
      rowCount: rejected.length,
    });
  };

  useEffect(() => {
    const resetDragState = () => {
      dragCounterRef.current = 0;
      isFileDragRef.current = false;
      setIsDragging(false);
      onDragLeave?.();
    };

    // Counter tracks nested elements: +1 on enter, -1 on leave, show overlay when counter goes from 0→1
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (isInternalDragRef.current) return;
      dragCounterRef.current++;

      if (e.dataTransfer?.types.includes('Files')) {
        isFileDragRef.current = true;
      }

      if (
        dragCounterRef.current === 1 &&
        e.dataTransfer?.types.includes('Files') &&
        modalStore.allowDragDropOverlay
      ) {
        flushSync(() => {
          setIsDragging(true);
        });
        onDragEnter?.();
      }
    };

    // Hide overlay when counter reaches 0 (cursor left the document)
    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();

      // If relatedTarget is inside the document, we're moving between elements, not leaving.
      // This fixes unbalanced events from virtualized components like DataTable.
      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && document.documentElement.contains(relatedTarget)) {
        return;
      }

      dragCounterRef.current--;
      if (dragCounterRef.current <= 0) {
        resetDragState();
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      resetDragState();
      const wasInternal = isInternalDragRef.current;
      isInternalDragRef.current = false;
      if (wasInternal) return;

      if (!modalStore.allowDragDropOverlay) return;

      onDrop?.();

      if (!e.dataTransfer) return;

      if (!e.dataTransfer.types.includes('Files')) {
        return;
      }

      const fileEntries = Array.from(e.dataTransfer.items)
        .map((item) => item.webkitGetAsEntry())
        .filter((entry) => entry !== null);

      const { files, rejected } = await getMediaFilesFromFileEntries(fileEntries);

      if (files.length > 0) {
        onFilesDropped?.(files, rejected);
        return;
      } else {
        // No files accepted — push rejects as library-scoped (untagged) so they
        // still appear in the library-wide failed list. The batch-scoped Results
        // modal only cares about rejects that belong to an actual upload batch.
        if (rejected.length > 0) {
          runInAction(() => {
            for (const r of rejected) {
              uploadManager.rejectedFiles.push({
                file: r.file,
                reason: r.reason,
                category: 'other',
                status: 'failed',
              });
            }
          });
        }
        pushRejectedRichEvent(rejected);
      }
    };

    // Fallback: reset when window loses focus
    const handleWindowBlur = () => {
      if (dragCounterRef.current > 0) {
        resetDragState();
      }
    };

    // Fallback: reset when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden && dragCounterRef.current > 0) {
        resetDragState();
      }
    };

    // Fallback for multi-monitor: reset if cursor re-enters with no buttons pressed.
    // Skip during OS file drags — Chrome reports buttons===0 for external file drags,
    // which would falsely trigger this reset and prevent the drop event from firing.
    const handleMouseEnter = (e: MouseEvent) => {
      if (dragCounterRef.current > 0 && e.buttons === 0 && !isFileDragRef.current) {
        resetDragState();
      }
    };

    const handleMouseLeave = () => {
      if (dragCounterRef.current > 0) {
        resetDragState();
      }
    };

    const handleDragStart = () => {
      isInternalDragRef.current = true;
    };
    // Three ways out, because `dragend` alone is not enough: it fires on the drag's source, and a
    // card or row moved into another folder has left the page by then, so it never reaches the
    // document. The `drop` above covers that case, and a mouse move — which no browser fires while
    // a drag is in progress — covers a drag abandoned with no drop at all.
    const handleDragEnd = () => {
      isInternalDragRef.current = false;
    };

    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('mousemove', handleDragEnd, { passive: true });
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.documentElement.addEventListener('mouseenter', handleMouseEnter);

    if (enableMouseLeaveHandler) {
      document.documentElement.addEventListener('mouseleave', handleMouseLeave);
      document.body.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (dragCounterRef.current > 0) {
        setIsDragging(false);
        onDragLeave?.();
      }
      dragCounterRef.current = 0;
      isFileDragRef.current = false;

      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('mousemove', handleDragEnd);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.documentElement.removeEventListener('mouseenter', handleMouseEnter);

      if (enableMouseLeaveHandler) {
        document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
        document.body.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [
    uploadManager,
    modalStore,
    enableMouseLeaveHandler,
    onDragEnter,
    onDragLeave,
    onDrop,
    onFilesDropped,
  ]);

  return { isDragging };
};
